import React from 'react';
import Seo from '../../components/Seo/Seo';

const PrivacyPolicy = () => {
  const lastUpdated = 'January 1, 2024';

  return (
    <div>
      <Seo
        title="Privacy Policy"
        description="Read how The Skilled Group collects, uses, and protects customer information across our website and services."
        canonicalPath="/privacy-policy"
      />
      {/* Page Header */}
      <div className="page-header">
        <div className="container-custom">
          <h1>Privacy Policy</h1>
          <p>Last updated: {lastUpdated}</p>
        </div>
      </div>

      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto prose prose-lg max-w-none">
            {/* Introduction */}
            <div className="mb-10">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                1. Introduction
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                The Skilled Group ("we", "our", "us") is committed to protecting the privacy
                of our customers and website visitors. This Privacy Policy explains how we
                collect, use, disclose, and safeguard your personal information when you
                visit our website or use our cleaning services.
              </p>
              <p className="text-gray-600 leading-relaxed">
                By using our website or services, you consent to the practices described in
                this Privacy Policy. If you do not agree with the terms of this policy,
                please do not access the website or use our services.
              </p>
            </div>

            {/* Information we collect */}
            <div className="mb-10">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                2. Information We Collect
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We may collect the following types of personal information:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>
                  <strong>Contact Information:</strong> Name, email address, phone number,
                  and physical address provided through our booking, quote, contact, or
                  feedback forms.
                </li>
                <li>
                  <strong>Service Information:</strong> Details about the cleaning services
                  you request, including service type, preferred dates and times, and
                  property details.
                </li>
                <li>
                  <strong>Feedback Information:</strong> Your name, rating, and comments
                  submitted through our feedback form.
                </li>
                <li>
                  <strong>Technical Information:</strong> IP address, browser type, device
                  information, and browsing patterns collected automatically when you visit
                  our website.
                </li>
                <li>
                  <strong>Communication Records:</strong> Records of correspondence between
                  you and The Skilled Group, including emails and phone calls.
                </li>
              </ul>
            </div>

            {/* How we use information */}
            <div className="mb-10">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                3. How We Use Your Information
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We use the information we collect for the following purposes:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>To process and manage your cleaning service bookings.</li>
                <li>To provide you with quotes and respond to your enquiries.</li>
                <li>To send booking confirmations and service-related communications.</li>
                <li>To display approved feedback on our Testimonials page.</li>
                <li>To improve our website, services, and customer experience.</li>
                <li>To comply with legal obligations and resolve disputes.</li>
                <li>To send promotional communications (only with your consent).</li>
              </ul>
            </div>

            {/* Information sharing */}
            <div className="mb-10">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                4. Information Sharing and Disclosure
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We do not sell, trade, or rent your personal information to third parties.
                We may share your information in the following limited circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>
                  <strong>Service Providers:</strong> With trusted third-party service
                  providers who assist us in operating our business (e.g., email services,
                  payment processors), under strict confidentiality agreements.
                </li>
                <li>
                  <strong>Legal Requirements:</strong> When required by law, regulation, or
                  legal process, or to protect the rights, property, or safety of The Skilled Group,
                  our customers, or others.
                </li>
                <li>
                  <strong>Approved Feedback:</strong> If you submit feedback and it is
                  approved, your name and feedback message will be displayed publicly on our
                  Testimonials page.
                </li>
              </ul>
            </div>

            {/* Data security */}
            <div className="mb-10">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                5. Data Security
              </h2>
              <p className="text-gray-600 leading-relaxed">
                We implement appropriate technical and organisational measures to protect
                your personal information against unauthorised access, alteration,
                disclosure, or destruction. However, no method of transmission over the
                Internet or method of electronic storage is 100% secure, and we cannot
                guarantee its absolute security.
              </p>
            </div>

            {/* Data retention */}
            <div className="mb-10">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                6. Data Retention
              </h2>
              <p className="text-gray-600 leading-relaxed">
                We retain your personal information for as long as necessary to fulfil the
                purposes outlined in this Privacy Policy, unless a longer retention period
                is required or permitted by law. Booking and service records are retained
                for a minimum of 7 years for tax and legal compliance purposes.
              </p>
            </div>

            {/* Your rights */}
            <div className="mb-10">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                7. Your Rights
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Under the Australian Privacy Act 1988, you have the right to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Access the personal information we hold about you.</li>
                <li>Request correction of inaccurate or outdated information.</li>
                <li>Request deletion of your personal information (subject to legal obligations).</li>
                <li>Opt out of promotional communications at any time.</li>
                <li>Lodge a complaint with the Office of the Australian Information Commissioner (OAIC).</li>
              </ul>
            </div>

            {/* Cookies */}
            <div className="mb-10">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                8. Cookies
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Our website may use cookies and similar tracking technologies to enhance
                your browsing experience. You can set your browser to refuse all or some
                browser cookies, or to alert you when cookies are being sent. If you
                disable cookies, some features of our website may not function properly.
              </p>
            </div>

            {/* Changes */}
            <div className="mb-10">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                9. Changes to This Policy
              </h2>
              <p className="text-gray-600 leading-relaxed">
                We may update this Privacy Policy from time to time. Any changes will be
                posted on this page with an updated "Last Updated" date. We encourage you
                to review this Privacy Policy periodically to stay informed about how we
                are protecting your information.
              </p>
            </div>

            {/* Contact */}
            <div className="mb-4">
              <h2 className="text-2xl font-heading font-bold text-gray-800 mb-4">
                10. Contact Us
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                If you have any questions about this Privacy Policy or our data practices,
                please contact us:
              </p>
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                <p className="text-gray-700 font-semibold mb-2">The Skilled Group</p>
                <p className="text-gray-600 text-sm">Email: info@theskilledgroup.com</p>
                <p className="text-gray-600 text-sm">Phone: 0466708090</p>
                <p className="text-gray-600 text-sm">Service Area: Brisbane, QLD</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;